import React, { useState, useRef, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiMenu, FiMessageSquare, FiSun, FiMoon, FiX, FiGrid } from "react-icons/fi";
import { useTheme } from "../../context/ThemeContext";
import { navConfig, parallaxNavConfig } from "../../config/navConfig";
import { appVariant, isSection } from "../../config/appVariant";

const navSections = appVariant.nav === 'parallax'
	? parallaxNavConfig
	: navConfig.filter(s => isSection(s.id));

// =============================================================================
// SWIPE TO CLOSE HOOK - iOS-style gesture
// =============================================================================

const useSwipeToClose = (onClose, isOpen, direction = 'down') => {
	const touchStart = useRef({ x: 0, y: 0 });
	const touchEnd = useRef({ x: 0, y: 0 });
	const minSwipeDistance = 80;

	const onTouchStart = useCallback((e) => {
		touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
		touchEnd.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
	}, []);

	const onTouchMove = useCallback((e) => {
		touchEnd.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
	}, []);

	const onTouchEnd = useCallback(() => {
		if (!isOpen) return;

		const deltaX = Math.abs(touchEnd.current.x - touchStart.current.x);
		const deltaY = touchEnd.current.y - touchStart.current.y;

		// Only trigger if vertical swipe is dominant
		if (deltaX > Math.abs(deltaY) * 0.5) return;

		if (direction === 'down' && deltaY > minSwipeDistance) {
			onClose();
		}
	}, [isOpen, onClose, direction]);

	return { onTouchStart, onTouchMove, onTouchEnd };
};

// =============================================================================
// MOBILE NAVIGATION DRAWER - Slide-up bottom sheet
// =============================================================================

const MobileNavDrawer = ({ isOpen, onClose, onOpenLazlo }) => {
	const location = useLocation();
	const { mode, toggleMode } = useTheme();
	const swipeHandlers = useSwipeToClose(onClose, isOpen, 'down');

	const isLinkActive = (to) => {
		if (location.pathname === to) return true;
		const basePath = to.split("/")[1] ? `/${to.split("/")[1]}` : to;
		return location.pathname.startsWith(basePath + "/");
	};

	const isSectionActive = (section) => {
		if (section.links.length === 0) return isLinkActive(section.homePath);
		return section.links.some(link => isLinkActive(link.to));
	};

	return (
		<>
			<div
				className={`mobile-nav-overlay ${isOpen ? 'mobile-nav-overlay--visible' : ''}`}
				onClick={onClose}
			/>
			<div
				className={`mobile-nav-drawer ${isOpen ? 'mobile-nav-drawer--open' : ''}`}
				{...swipeHandlers}
			>
				<div className="mobile-nav-drawer__handle" />
				<div className="mobile-nav-drawer__header">
					<h2 className="mobile-nav-drawer__title">Navigation</h2>
					<button className="mobile-nav-drawer__close" onClick={onClose}>
						<FiX />
					</button>
				</div>
				<div className="mobile-nav-drawer__content">
					<NavLink
						to={appVariant.defaultRoute}
						className={`mobile-nav-section__header ${location.pathname === appVariant.defaultRoute ? 'mobile-nav-section__header--active' : ''}`}
						onClick={onClose}
					>
						<FiGrid className="mobile-nav-section__icon" />
						<span className="mobile-nav-section__label">Dashboard</span>
					</NavLink>
					{navSections.map((section) => {
						const Icon = section.icon;
						const isActive = isSectionActive(section);

						return (
							<div key={section.id} className="mobile-nav-section">
								<NavLink
									to={section.homePath}
									className={`mobile-nav-section__header ${isActive ? 'mobile-nav-section__header--active' : ''}`}
									onClick={onClose}
								>
									<Icon className="mobile-nav-section__icon" />
									<span className="mobile-nav-section__label">{section.title}</span>
								</NavLink>
								<div className="mobile-nav-section__links">
									{section.links.map((link) => (
										<NavLink
											key={link.to}
											to={link.to}
											className={`mobile-nav-link ${isLinkActive(link.to) ? 'mobile-nav-link--active' : ''}`}
											onClick={onClose}
										>
											{link.label}
										</NavLink>
									))}
								</div>
							</div>
						);
					})}
				</div>
				<div className="mobile-nav-drawer__footer">
					<button
						className={`mobile-nav-footer-btn mobile-nav-footer-btn--toggle ${mode === 'dark' ? 'mobile-nav-footer-btn--active' : ''}`}
						onClick={toggleMode}
					>
						{mode === 'light' ? <FiSun /> : <FiMoon />}
						<span>{mode === 'light' ? 'Light' : 'Dark'}</span>
					</button>
					{appVariant.showLazlo && (
						<button
							className="mobile-nav-footer-btn"
							onClick={() => { onOpenLazlo(); onClose(); }}
						>
							<FiMessageSquare />
							<span>Lazlo</span>
						</button>
					)}
				</div>
			</div>
		</>
	);
};

// =============================================================================
// MOBILE BOTTOM NAV - Fixed bottom bar with contextual section links
// =============================================================================

const MobileBottomNav = ({ onOpenMenu, onOpenLazlo }) => {
	const location = useLocation();

	// Find current section based on route
	const getCurrentSection = () => {
		for (const section of navSections) {
			if (location.pathname === section.homePath ||
				location.pathname.startsWith(section.homePath + "/") ||
				section.links.some(link => location.pathname.startsWith(link.to.split("/")[1] ? `/${link.to.split("/")[1]}` : link.to))) {
				return section;
			}
		}
		return navSections[0]; // Default to first section
	};

	const currentSection = getCurrentSection();

	const isLinkActive = (to) => {
		if (location.pathname === to) return true;
		const basePath = to.split("/")[1] ? `/${to.split("/")[1]}` : to;
		return location.pathname.startsWith(basePath + "/");
	};

	return (
		<nav className="mobile-bottom-nav">
			<button className="mobile-bottom-nav__item mobile-bottom-nav__item--menu" onClick={onOpenMenu}>
				<FiMenu className="mobile-bottom-nav__icon" />
				<span className="mobile-bottom-nav__label">Menu</span>
			</button>
			<div className="mobile-bottom-nav__section-links">
				{currentSection.links.slice(0, 3).map((link) => (
					<NavLink
						key={link.to}
						to={link.to}
						className={`mobile-bottom-nav__item ${isLinkActive(link.to) ? 'mobile-bottom-nav__item--active' : ''}`}
					>
						<span className="mobile-bottom-nav__label">{link.label}</span>
					</NavLink>
				))}
			</div>
			{appVariant.showLazlo && (
				<button className="mobile-bottom-nav__item mobile-bottom-nav__item--chat" onClick={onOpenLazlo}>
					<FiMessageSquare className="mobile-bottom-nav__icon" />
					<span className="mobile-bottom-nav__label">Lazlo</span>
				</button>
			)}
		</nav>
	);
};

// =============================================================================
// MOBILE NAV - Combined component
// =============================================================================

const MobileNav = ({ isLazloOpen, onLazloToggle }) => {
	const [drawerOpen, setDrawerOpen] = useState(false);

	const openDrawer = () => setDrawerOpen(true);
	const closeDrawer = () => setDrawerOpen(false);

	return (
		<>
			<MobileBottomNav
				onOpenMenu={openDrawer}
				onOpenLazlo={onLazloToggle}
			/>
			<MobileNavDrawer
				isOpen={drawerOpen}
				onClose={closeDrawer}
				onOpenLazlo={onLazloToggle}
			/>
		</>
	);
};

export default MobileNav;
